// src/components/pages/map/PinDetailsPage.js
import React, { useRef, useEffect, useState, useLayoutEffect } from "react";
import { useParams } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import useGeolocationMap from "../../../hooks/useGeolocationMap";
import { NAVBAR_HEIGHT } from "../../../constants/constants";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../../firebase";
import TimeCapsuleCard from "../../TimeCapsuleCard";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

const PinDetailsPage = () => {
  const { id } = useParams();
  const [capsule, setCapsule] = useState(null);
  const [loadingCapsule, setLoadingCapsule] = useState(true);

  const [mapDynamicHeight, setMapDynamicHeight] = useState(
    `calc(100vh - ${NAVBAR_HEIGHT}px)`
  );
  const mapContainerRef = useRef(null);
  const cardRef = useRef(null);

  const fallback = { lng: 0, lat: 0, zoom: 16 };
  const mapCenter = capsule?.location
    ? { lng: capsule.location.lng, lat: capsule.location.lat, zoom: 16 }
    : fallback;

  const { geolocationLoaded, map } = useGeolocationMap(
    mapContainerRef,
    mapCenter,
    "mapbox://styles/jaimeboyaca/cm7f1viks00ad01qsa8cy3xdb",
    null,
    false,
    capsule ? [createPin(capsule)] : []
  );

  function createPin(c) {
    const { location, capsuleType } = c;
    let icon, w, h;
    switch (capsuleType) {
      case "private":
        icon = require("../../../assets/icons/icon-time-capsule-locked.png");
        w = "48px"; h = "75px"; break;
      case "memberExclusive":
        icon = require("../../../assets/icons/icon-treasure-open-marker.png");
        w = "90px"; h = "80px"; break;
      default:
        icon = require("../../../assets/icons/icon-marker-public.svg");
        w = "60px"; h = "60px";
    }
    return {
      id: c.id,
      lng: location.lng,
      lat: location.lat,
      markerWidth: w,
      markerHeight: h,
      icon,
      onClick: () => {},
    };
  }

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, "timeCapsules", id));
        if (snap.exists()) setCapsule({ id: snap.id, ...snap.data() });
        else console.error("No such time capsule!");
      } catch (err) {
        console.error(err);
      }
      setLoadingCapsule(false);
    };
    load();
  }, [id]);

  // ←— here’s the change: jumpTo instead of flyTo
  useEffect(() => {
    if (map && capsule?.location) {
      map.jumpTo({
        center: [capsule.location.lng, capsule.location.lat],
        zoom: 16,
      });
    }
  }, [map, capsule]);

  useLayoutEffect(() => {
    const measure = () => {
      if (cardRef.current) {
        const h = cardRef.current.getBoundingClientRect().height;
        setMapDynamicHeight(`${window.innerHeight - h - NAVBAR_HEIGHT}px`);
      }
    };
    measure();
    const t = setTimeout(measure, 200);
    const obs = new ResizeObserver(measure);
    if (cardRef.current) obs.observe(cardRef.current);
    window.addEventListener("resize", measure);
    return () => {
      clearTimeout(t);
      obs.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  useEffect(() => {
    if (map) map.resize();
  }, [mapDynamicHeight, map]);

  return (
    <Box sx={{ width: "100vw", height: "100vh" }}>
      <Box ref={cardRef}>
        {loadingCapsule ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: 200,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <TimeCapsuleCard
            capsule={capsule}
            containerRef={cardRef}
            onLoad={() => {}}
          />
        )}
      </Box>

      {!geolocationLoaded ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100vw",
            height: mapDynamicHeight,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ width: "100vw", height: mapDynamicHeight }}>
          <Box ref={mapContainerRef} sx={{ width: "100%", height: "100%" }} />
        </Box>
      )}
    </Box>
  );
};

export default PinDetailsPage;
